import { TableCell, Typography } from "@mui/material";
import type { SxProps } from "@mui/material";
import { MouseEventHandler, forwardRef } from "react";
import { headerColor } from "../constants";
import CustomTooltip from "./CustomTooltip";

type Props = {
  children?: any;
  onClick?: MouseEventHandler<HTMLTableCellElement>;
  sx?: SxProps;
  colSpan?: number;
  rowSpan?: number;
  isLineVisible?: boolean;
  gray?: boolean;
  multi?: boolean;
  right?: boolean;
  center?: boolean;
};

const fontSizeHeader = 16;
const fontSize = 16;
const defaultRowSpan = 1;
const defaultHeaderRowSpan = 1;

const styles: { [key: string]: SxProps } = {
  multiEllipsis: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    fontSize,
    maxHeight: 51,
    minWidth: 30,
    overflow: "hidden",
    overflowWrap: "break-word",
  },
  tooltipEllipsis: {
    fontSize,
    minWidth: 30,
    overflowWrap: "break-word",
  },
  lineVisible: {
    borderLeft: "1px solid var(--offwhite-600)",
  },
};

// /**
//  * 指定行入力し、指定行をはみ出した場合はellipsisにするコンポーネント
//  * @param children 子コンポーネント
//  * @returns JSX.Element
//  */
// export function MultiTypography({
//   children,
//   sx,
// }: {
//   children: JSX.Element | string | null;
//   sx?: any;
// }) {
//   return (
//     <Typography component="span" sx={{ ...styles.multiEllipsis, ...(sx ?? {}) }}>
//       {children}
//     </Typography>
//   );
// }

/**
 * 指定行入力し、指定行をはみ出した場合はellipsisにするコンポーネント
 * @param children 子コンポーネント
 * @returns JSX.Element
 */
export function MultiTypography({
  children,
  sx,
  noTooltip,
}: {
  children: JSX.Element | string | null;
  sx?: any;
  noTooltip?: boolean;
}) {
  if (noTooltip) {
    return (
      <Typography component="span" sx={{ ...styles.multiEllipsis, ...(sx ?? {}) }}>
        {children}
      </Typography>
    );
  }
  return (
    <CustomTooltip title={children} disableInteractive arrow>
      <Typography component="span" sx={{ ...styles.multiEllipsis, ...(sx ?? {}) }}>
        {children}
      </Typography>
    </CustomTooltip>
  );
}

/**
 *
 * @param root0 コンポーネントのプロップス
 */
export default forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
    }: Props,
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        maxWidth: 110,
        paddingX: 1,
        paddingY: 1,
        textAlign: "center",
        ...(gray ? { backgroundColor: "rgb(255, 255, 230)" } : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {multi ? <MultiTypography>{children}</MultiTypography> : children}
    </TableCell>
  ),
);

export const TableCellWide = forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
    }: Props,
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        // minWidth: 99,
        // maxWidth: 160,
        minWidth: 99,
        // maxWidth: 180, // 広めに
        maxWidth: 200,
        width: 'clamp(99px, 200px, 200px)',
        paddingY: 0,
        paddingX: 1,
        textAlign: "center",
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        ...(gray ? { backgroundColor: "rgb(255, 255, 230)" } : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {multi ? <MultiTypography>{children}</MultiTypography> : children}
    </TableCell>
  ),
);

export const TableCellNarrow = forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
    }: Props,
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        // minWidth: 40,
        // maxWidth: 50,
        minWidth: 80,
        maxWidth: 80, // 広めに固定で
        paddingX: 1,
        paddingY: 1,
        textAlign: "center",
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        ...(gray ? { backgroundColor: "rgb(255, 255, 230)" } : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {multi ? <MultiTypography>{children}</MultiTypography> : children}
    </TableCell>
  ),
);

// テーブルヘッダー用
export const TableHeaderCellSmall = forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
      isTwoHeight = false, // 二行分の高さ
    }: Props & {isTwoHeight?: boolean},
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize: fontSizeHeader,
        fontWeight: 'bold',
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: 110,
        paddingX: 1,
        paddingY: 1,
        textAlign: "center",
        ...(isTwoHeight ? {height: 48} : {}),
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// テーブルヘッダー用の商品名など幅の大きなものに使用
export const TableHeaderCellWide = forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
      isTwoHeight = false, // 二行分の高さ
    }: Props & {isTwoHeight?: boolean},
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize: fontSizeHeader,
        fontWeight: 'bold',
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        minWidth: 99,
        // maxWidth: 160,
        maxWidth: 200,
        width: 'clamp(99px, 200px, 200px)',
        paddingY: 0,
        paddingX: 1,
        textAlign: "center",
        ...(isTwoHeight ? {height: 48} : {}),
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// テーブルヘッダー用の在庫数など幅の小さなものに使用
export const TableHeaderCellNarrow = forwardRef(
  (
    {
      children,
      onClick,
      sx,
      colSpan,
      rowSpan,
      isLineVisible = true,
      gray = false,
      multi = false,
      right = false,
      center = false,
      isTwoHeight = false, // 二行分の高さ
    }: Props & {isTwoHeight?: boolean},
    ref,
  ) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize: fontSizeHeader,
        fontWeight: 'bold',
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        minWidth: 80,
        maxWidth: 80,
        paddingX: 1,
        textAlign: "center",
        ...(isTwoHeight ? {height: 48} : {}),
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(center ? { textAlign: "center" } : {}),
        // ...(right ? { textAlign: "right" } : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);
